import * as Icons from "@nova/component-icons"
import { GeometryType } from "@nova/constants"
import { Line } from "@nova/protobuf"
import { selectLineById } from "features/entity-caching/lines/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { PluginSpec } from "plugins/plugin-registry"
import { setActiveTool } from "plugins/Toolbox/tool-registry"
import { useCallback } from "react"
import { GenericInspectorPanelConfirmControls } from "../generics/GenericInspectorPanelConfirmControls"
import {
    GenericInspectorPanelFooterContainer,
    getGenericInspectorPanelFooter,
} from "../inspector-panels/footers/GenericInspectorPanelFooter"
import { getGenericInspectorPanelComponent } from "../inspector-panels/GenericInspectorPanel"
import {
    GenericInspectorPanelHeaderActions,
    GenericInspectorPanelHeaderTopRow,
    GenericInspectorPanelRenamableLayerName,
    GenericInspectorPanelSubtitles,
} from "../inspector-panels/headers/GenericInspectorPanelHeader"
import { InspectorPanelPlugin } from "../useInspectorPanel"
import { InspectorPanelLineData } from "./InspectorPanelLineData"
import { InspectorPanelLineLayer } from "./layer/InspectorPanelLineLayer"
import { clearEditingData, selectLineEditingData } from "plugins/Toolbox/Line/LineSlice"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { getGenericInspectorPanelBody } from "../inspector-panels/bodies/GenericInspectorPanelBody"

function EditingLineHeader({ id }: { id: string }) {
    const line = useAppSelector(selectLineById(id))
    return (
        <div className="flex flex-col px-4 pt-4 pb-2">
            <GenericInspectorPanelHeaderTopRow
                typeTitle={`Edit ${line.geometryType === GeometryType.Line ? "Line" : "Polygon"}`}
                Icon={Icons.Polyline}
            />
        </div>
    )
}

function EditingLineFooter({}: { id: string }) {
    const dispatch = useAppDispatch()

    const handleBlur = useCallback(
        (save = true) => {
            geometryDataStore.free(save)
            dispatch(setActiveTool(undefined))
        },
        [dispatch]
    )
    useEffectUnsafe(() => {
        return () => {
            geometryDataStore.free(true)
            dispatch(clearEditingData())
        }
    }, [])

    return (
        <GenericInspectorPanelFooterContainer>
            <GenericInspectorPanelConfirmControls
                onCancel={() => handleBlur(false)}
                onConfirm={handleBlur}
                canConfirm={true}
            />
        </GenericInspectorPanelFooterContainer>
    )
}

export function getInspectorPanelLine(spec: PluginSpec<Line>) {
    const InspectorPanelLineLayerNoTopBorder = ({ id }: { id?: string }) => {
        return <InspectorPanelLineLayer id={id} shouldShowTopBorder={false} />
    }
    const LineInspectorPanel = getGenericInspectorPanelComponent({
        spec,
        Header: getLineInspectorPanelHeader(spec),
        Body: getGenericInspectorPanelBody({
            LayerComponent: InspectorPanelLineLayerNoTopBorder,
            DataComponent: InspectorPanelLineData,
        }),
        Footer: getGenericInspectorPanelFooter(),
    })
    const EditingLineInspectorPanel = getGenericInspectorPanelComponent({
        spec,
        Header: EditingLineHeader,
        Body: InspectorPanelLineLayer,
        Footer: EditingLineFooter,
    })
    const InspectorPanelLine = ({ id, plugin }: { id: string; plugin: InspectorPanelPlugin }) => {
        const editingData = useAppSelector(selectLineEditingData)
        if (!editingData || editingData.id !== id) {
            return <LineInspectorPanel id={id} plugin={plugin} />
        }
        return <EditingLineInspectorPanel id={id} plugin={plugin} />
    }
    return InspectorPanelLine
}

export function getLineInspectorPanelHeader(spec: PluginSpec<Line>) {
    const GenericInspectorPanelHeader = ({
        id,
        plugin,
    }: {
        id: string
        plugin: InspectorPanelPlugin
    }) => {
        const line = useAppSelector(spec.selectSingleEntity(id))
        return (
            <div className="flex flex-col px-4 pt-4 pb-3">
                <GenericInspectorPanelHeaderTopRow
                    typeTitle={line.geometryType === GeometryType.Line ? "Line" : "Polygon"}
                />
                <GenericInspectorPanelRenamableLayerName
                    id={id}
                    updateFn={spec.updateAction}
                    className="mt-[14px] mb-2"
                />
                <GenericInspectorPanelSubtitles id={id} plugin={plugin} />
                <GenericInspectorPanelHeaderActions id={id} plugin={plugin} />
            </div>
        )
    }
    return GenericInspectorPanelHeader
}
